import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  Wallet,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { userApi } from '../../api';
import { UserStats } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { NotificationCenter } from '../../components/NotificationCenter';
import { PaymentHistoryChart } from '../../components/PaymentHistoryChart';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await userApi.getStats();
        if (response.success && response.data) {
          setStats(response.data.stats);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const calculateDaysUntil = (dueDate: string): number => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getPaymentUrgency = (daysUntil: number): string => {
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 3) return 'urgent';
    if (daysUntil <= 7) return 'warning';
    return 'normal';
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's an overview of your account and loan activity</p>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {stats && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">
                <Wallet size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Loans</div>
                <div className="stat-value">{stats.total_loans}</div>
              </div>
            </div>

            <div className="stat-card stat-card-active">
              <div className="stat-icon">
                <Clock size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Active Loans</div>
                <div className="stat-value">{stats.active_loans}</div>
              </div>
            </div>

            <div className="stat-card stat-card-success">
              <div className="stat-icon">
                <CheckCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Completed</div>
                <div className="stat-value">{stats.paid_loans}</div>
              </div>
            </div>

            <div className="stat-card stat-card-warning">
              <div className="stat-icon">
                <AlertCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Outstanding Balance</div>
                <div className="stat-value stat-value-currency">
                  {formatCurrency(stats.total_outstanding)}
                </div>
              </div>
            </div>

            <div className="stat-card stat-card-info">
              <div className="stat-icon">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Repaid</div>
                <div className="stat-value stat-value-currency">
                  {formatCurrency(stats.total_paid)}
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">
                <FileText size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-label">Pending Applications</div>
                <div className="stat-value">{stats.pending_applications}</div>
              </div>
            </div>
          </div>

          {stats.upcoming_payment && (() => {
            const daysUntil = calculateDaysUntil(stats.upcoming_payment.due_date);
            const urgency = getPaymentUrgency(daysUntil);

            return (
              <div className={`upcoming-payment-widget ${urgency}`}>
                <div className="widget-header">
                  <h3>
                    {daysUntil < 0 ? 'Overdue Payment!' : 'Upcoming Payment'}
                  </h3>
                  <div className={`countdown ${urgency}`}>
                    {daysUntil < 0 ? (
                      <span className="overdue-text">{Math.abs(daysUntil)} days overdue</span>
                    ) : daysUntil === 0 ? (
                      <span className="due-today">Due Today!</span>
                    ) : (
                      <span className="days-remaining">
                        {daysUntil} {daysUntil === 1 ? 'day' : 'days'} remaining
                      </span>
                    )}
                  </div>
                </div>

                <div className="payment-details">
                  <div className="payment-item">
                    <span className="label">Loan:</span>
                    <span className="value">{stats.upcoming_payment.loan_reference}</span>
                  </div>
                  <div className="payment-item">
                    <span className="label">Due Date:</span>
                    <span className="value">
                      {new Date(stats.upcoming_payment.due_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="payment-item">
                    <span className="label">Amount:</span>
                    <span className="value amount-highlight">
                      {formatCurrency(stats.upcoming_payment.total_amount)}
                    </span>
                  </div>
                </div>

                <div className="widget-actions">
                  <Button
                    onClick={() => navigate('/customer/loans')}
                    variant="primary"
                  >
                    Make Payment
                  </Button>
                  <Button
                    onClick={() => navigate('/customer/loans')}
                    variant="secondary"
                  >
                    View Loan Details
                  </Button>
                </div>
              </div>
            );
          })()}
        </>
      )}

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <Link to="/customer/applications/new" className="action-card action-card-primary">
            <div className="action-icon">
              <Plus size={28} />
            </div>
            <div className="action-title">Apply for Loan</div>
            <div className="action-description">Start a new loan application</div>
          </Link>

          <Link to="/customer/applications" className="action-card">
            <div className="action-icon">
              <FileText size={28} />
            </div>
            <div className="action-title">Applications</div>
            <div className="action-description">Track your applications</div>
          </Link>

          <Link to="/customer/loans" className="action-card">
            <div className="action-icon">
              <Wallet size={28} />
            </div>
            <div className="action-title">My Loans</div>
            <div className="action-description">View and manage loans</div>
          </Link>

          <Link to="/loan-products" className="action-card">
            <div className="action-icon">
              <TrendingUp size={28} />
            </div>
            <div className="action-title">Loan Products</div>
            <div className="action-description">Explore loan options</div>
          </Link>
        </div>
      </div>

      <PaymentHistoryChart />

      <NotificationCenter />

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .actions-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .dashboard-page {
            padding: 1rem;
          }

          .page-header {
            margin-bottom: 1.5rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 0.875rem;
          }

          .stat-card {
            padding: 1.25rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .stat-value-currency {
            font-size: 1.25rem;
          }

          .upcoming-payment-widget {
            padding: 1.25rem;
            margin-bottom: 1.5rem;
          }

          .widget-header h3 {
            font-size: 1.125rem;
          }

          .payment-details {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .widget-actions {
            flex-direction: column;
            gap: 0.75rem;
          }

          .widget-actions button {
            width: 100%;
          }

          .quick-actions h3 {
            font-size: 1.25rem;
          }

          .actions-grid {
            grid-template-columns: 1fr;
            gap: 0.875rem;
          }

          .action-card {
            padding: 1.25rem;
          }

          .action-icon {
            width: 2.5rem;
            height: 2.5rem;
          }

          .action-icon svg {
            width: 20px;
            height: 20px;
          }

          .action-title {
            font-size: 1.0625rem;
          }

          .action-description {
            font-size: 0.875rem;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .dashboard-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-icon {
            width: 2.5rem;
            height: 2.5rem;
          }

          .stat-icon svg {
            width: 18px;
            height: 18px;
          }

          .upcoming-payment-widget {
            padding: 1rem;
          }

          .countdown {
            font-size: 0.875rem;
          }

          .payment-item {
            font-size: 0.875rem;
          }

          .action-card {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};
